import { useEffect } from "react";
import {
  Grid,
  IconButton,
  Stack,
  Typography,
  TextField,
} from "@pankod/refine-mui";
import { Controller } from "@pankod/refine-react-hook-form";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import AutoGeneratedInputCmp from "components/auto-generated-input/AutoGeneratedInputCmp";

function RepeatableItem({
  fields,
  onRemove,
  basePath,
  control,
  register,
  errors,
}) {
  return (
    <Stack
      spacing={2}
      sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 2 }}
    >
      {fields.map((field) => (
        <Grid item xs={12} key={field.key}>
          {field.type === "textarea" ? (
            <TextField
              {...register(`${basePath}.${field.name}`, {
                required: field.required ? `${field.label} is required` : false,
              })}
              label={field.label}
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              error={!!errors?.[field.name]}
              helperText={errors?.[field.name]?.message}
            />
          ) : (
            <TextField
              {...register(`${basePath}.${field.name}`, {
                required: field.required ? `${field.label} is required` : false,
              })}
              label={field.label}
              fullWidth
              variant="outlined"
              error={!!errors?.[field.name]}
              helperText={errors?.[field.name]?.message}
            />
          )}
        </Grid>
      ))}
      <Grid item xs={12}>
        <IconButton onClick={onRemove} color="error" size="small">
          <DeleteIcon />
        </IconButton>
      </Grid>
    </Stack>
  );
}

function RepeatableEditor({
  name,
  label,
  itemFields,
  control,
  register,
  errors,
  watch,
  setValue,
  defaultValue,
}) {
  const watchedValue = watch(name);
  const items = watchedValue || defaultValue || [];

  const handleAdd = () => {
    const newItem = {};
    itemFields.forEach((f) => (newItem[f.name] = ""));
    setValue(name, [...items, newItem], { shouldValidate: true });
  };

  const handleRemove = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setValue(name, newItems, { shouldValidate: true });
  };

  return (
    <Grid item xs={12}>
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue || []}
        render={() => (
          <>
            {items.map((_, index) => (
              <RepeatableItem
                key={index}
                fields={itemFields}
                basePath={`${name}[${index}]`}
                control={control}
                register={register}
                errors={errors?.[name]?.[index]}
                onRemove={() => handleRemove(index)}
              />
            ))}
            <IconButton onClick={handleAdd} color="primary" size="small">
              <AddIcon />
              <Typography variant="body2" sx={{ ml: 0.5 }}>
                Add
              </Typography>
            </IconButton>
          </>
        )}
      />
    </Grid>
  );
}

export default function CompFields({
  register,
  control,
  errors,
  defaultLanguageRecord,
  getValues,
  setError,
  setValue,
  watch,
  setFieldsSettings,
}) {
  const themesFields = [
    {
      key: "title",
      name: "title",
      label: "Title",
      type: "text",
      required: true,
    },
    {
      key: "description",
      name: "description",
      label: "Description",
      type: "textarea",
      required: false,
    },
  ];

  const endorsementsFields = [
    { key: "name", name: "name", label: "Name", type: "text", required: true },
    {
      key: "description",
      name: "description",
      label: "Description",
      type: "textarea",
      required: false,
    },
    {
      key: "quote",
      name: "quote",
      label: "Quote",
      type: "textarea",
      required: false,
    },
  ];

  const destinationsFields = [
    {
      key: "title",
      name: "title",
      label: "Title",
      type: "text",
      required: true,
    },
    { key: "url", name: "url", label: "URL", type: "text", required: true },
    {
      key: "description",
      name: "description",
      label: "Description",
      type: "textarea",
      required: false,
    },
  ];

  const fields = [
    {
      type: "text",
      name: "title",
      label: "Title",
    },
    {
      type: "text",
      name: "short_desc",
      label: "Subtitle",
      lines: 2,
    },
    {
      type: "number",
      name: "publicationYear",
      label: "Publication Year",
    },
    {
      type: "text",
      name: "description",
      label: "Commercial Info",
      lines: 3,
    },
    {
      label: "Image",
      name: "image",
      required: true,
      type: "image",
      width: "100%",
      height: 350,
      elevation: 0,
      md: 4,
    },
    {
      type: "text",
      name: "synopsis",
      label: "Synopsis",
      lines: 5,
    },
  ];

  useEffect(() => {
    setFieldsSettings(fields);
  }, []);

  return (
    <Grid container spacing={2} justifyContent="center">
      {fields.map((field, index) => {
        return (
          <AutoGeneratedInputCmp
            key={index}
            {...field}
            register={register}
            control={control}
            errors={errors}
            setError={setError}
            fieldVariants="outlined"
            defaultValue={watch(field.name)}
            defaultLanguageRecord={defaultLanguageRecord}
            getValues={getValues}
            setValue={setValue}
            watch={watch}
          />
        );
      })}

      <RepeatableEditor
        name="themes"
        label="Themes"
        itemFields={themesFields}
        control={control}
        register={register}
        errors={errors}
        watch={watch}
        setValue={setValue}
        defaultValue={watch("themes")}
      />

      <RepeatableEditor
        name="endorsments"
        label="Endorsements"
        itemFields={endorsementsFields}
        control={control}
        register={register}
        errors={errors}
        watch={watch}
        setValue={setValue}
        defaultValue={watch("endorsments")}
      />

      <RepeatableEditor
        name="destinations"
        label="Destinations"
        itemFields={destinationsFields}
        control={control}
        register={register}
        errors={errors}
        watch={watch}
        setValue={setValue}
        defaultValue={watch("destinations")}
      />
    </Grid>
  );
}
